import { useState, useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { QuestionSet, QuestionFolder } from '@/types';
import { FolderBreadcrumb } from '@/components/FolderBreadcrumb';
import { FolderCard } from '@/components/FolderCard';
import { CreateFolderModal } from '@/components/CreateFolderModal';
import { MoveToFolderModal } from '@/components/MoveToFolderModal';

interface QuestionManagementProps {
  onNavigateToEditor: (questionSetId?: string) => void;
}

const QuestionManagement = ({
  onNavigateToEditor,
}: QuestionManagementProps) => {
  const {
    questionSets,
    isLoadingQuestionSets,
    createQuestionSet,
    deleteQuestionSet,
    moveQuestionSetToFolder,
    folders,
    isLoadingFolders,
    createFolder,
    updateFolder,
    deleteFolder,
    moveFolder,
    currentFolderId,
    setCurrentFolder,
    getFolderPath,
    user,
  } = useAuthStore();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteFolderConfirmId, setDeleteFolderConfirmId] = useState<
    string | null
  >(null);
  const [moveItemModal, setMoveItemModal] = useState<{
    isOpen: boolean;
    itemId: string;
    itemName: string;
    itemType: 'question set' | 'folder';
    currentFolderId: string | null;
  } | null>(null);
  const [folderToEdit, setFolderToEdit] = useState<QuestionFolder | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    difficulty: 'mixed' as 'easy' | 'medium' | 'hard' | 'mixed',
    tags: '',
  });

  // Get current folder path for breadcrumb
  const folderPath = useMemo(
    () => getFolderPath(currentFolderId),
    [currentFolderId, getFolderPath]
  );

  // Filter question sets and folders for current folder
  const currentQuestionSets = useMemo(
    () =>
      questionSets.filter((qs) => (qs.folderId || null) === currentFolderId),
    [questionSets, currentFolderId]
  );

  const currentFolders = useMemo(
    () => folders.filter((f) => (f.parentFolderId || null) === currentFolderId),
    [folders, currentFolderId]
  );

  // Count items in each folder
  const getFolderCounts = (folderId: string) => {
    const questionSetCount = questionSets.filter(
      (qs) => qs.folderId === folderId
    ).length;
    const subFolderCount = folders.filter(
      (f) => f.parentFolderId === folderId
    ).length;
    return { questionSetCount, subFolderCount };
  };

  const handleCreateSet = async () => {
    if (!user || !formData.name.trim()) return;

    try {
      const newSet: Omit<QuestionSet, 'id' | 'createdAt' | 'updatedAt'> = {
        name: formData.name.trim(),
        description: formData.description.trim()
          ? formData.description.trim()
          : undefined,
        questions: [],
        createdBy: user.uid,
        isActive: true,
        totalQuestions: 0,
        estimatedDuration: 0,
        difficulty: formData.difficulty,
        tags: formData.tags.trim()
          ? formData.tags
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean)
          : undefined,
        status: 'draft', // New question sets start as drafts
        folderId: currentFolderId || undefined, // Save in current folder
      };

      const questionSetId = await createQuestionSet(newSet);
      console.log('✅ Question set created as draft:', questionSetId);

      // Reset form and close
      setFormData({ name: '', description: '', difficulty: 'mixed', tags: '' });
      setShowCreateForm(false);

      // Navigate to question editor for the new set
      onNavigateToEditor(questionSetId);
    } catch (error) {
      console.error('❌ Failed to create question set:', error);
    }
  };

  const handleCreateFolder = async (folderData: {
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    parentFolderId?: string;
  }) => {
    if (!user) return;

    try {
      await createFolder({
        ...folderData,
        createdBy: user.uid,
        parentFolderId: currentFolderId || undefined,
      });
      console.log('✅ Folder created');
      setShowCreateFolderModal(false);
      setFolderToEdit(null);
    } catch (error) {
      console.error('❌ Failed to create folder:', error);
      throw error;
    }
  };

  const handleUpdateFolder = async (folderData: {
    name: string;
    description?: string;
    icon?: string;
    color?: string;
  }) => {
    if (!folderToEdit) return;

    try {
      await updateFolder(folderToEdit.id, folderData);
      console.log('✅ Folder updated');
      setShowCreateFolderModal(false);
      setFolderToEdit(null);
    } catch (error) {
      console.error('❌ Failed to update folder:', error);
      throw error;
    }
  };

  const handleDeleteSet = async (setId: string) => {
    try {
      await deleteQuestionSet(setId);
      setDeleteConfirmId(null);
      console.log('✅ Question set deleted');
    } catch (error) {
      console.error('❌ Failed to delete question set:', error);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    try {
      await deleteFolder(folderId);
      setDeleteFolderConfirmId(null);
      console.log('✅ Folder deleted');
    } catch (error: unknown) {
      console.error('❌ Failed to delete folder:', error);
      alert(error instanceof Error ? error.message : 'Failed to delete folder');
    }
  };

  const handleMoveItem = async (targetFolderId: string | null) => {
    if (!moveItemModal) return;

    try {
      if (moveItemModal.itemType === 'question set') {
        await moveQuestionSetToFolder(moveItemModal.itemId, targetFolderId);
      } else {
        await moveFolder(moveItemModal.itemId, targetFolderId);
      }
      console.log('✅ Item moved successfully');
      setMoveItemModal(null);
    } catch (error: unknown) {
      console.error('❌ Failed to move item:', error);
      alert(error instanceof Error ? error.message : 'Failed to move item');
      throw error;
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return '🟢';
      case 'medium':
        return '🟡';
      case 'hard':
        return '🔴';
      case 'mixed':
        return '🌈';
      default:
        return '⚪';
    }
  };

  // Get descendant folder IDs (to prevent moving into own descendant)
  const getDescendantFolderIds = (folderId: string): string[] => {
    const descendants: string[] = [folderId];
    const children = folders.filter((f) => f.parentFolderId === folderId);
    children.forEach((child) => {
      descendants.push(...getDescendantFolderIds(child.id));
    });
    return descendants;
  };

  if (isLoadingQuestionSets || isLoadingFolders) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '60px',
              height: '60px',
              border: '4px solid rgba(102, 126, 234, 0.3)',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem',
            }}
          />
          <h3 style={{ color: '#374151', margin: '0 0 0.5rem' }}>
            Loading Question Sets
          </h3>
          <p style={{ color: '#64748b', margin: 0 }}>
            Please wait while we fetch your questions...
          </p>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: '1.5rem 2rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div>
          <h2
            style={{
              margin: '0 0 0.5rem',
              fontSize: '1.8rem',
              fontWeight: 'bold',
              color: '#374151',
            }}
          >
            📚 Question Sets
          </h2>
          <p
            style={{
              margin: 0,
              color: '#64748b',
              fontSize: '1rem',
            }}
          >
            Create and manage your quiz question collections
          </p>
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={() => setShowCreateFolderModal(true)}
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow =
                '0 6px 20px rgba(245, 158, 11, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow =
                '0 4px 15px rgba(245, 158, 11, 0.4)';
            }}
          >
            📁 New Folder
          </button>

          <button
            onClick={() => setShowCreateForm(true)}
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow =
                '0 6px 20px rgba(16, 185, 129, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow =
                '0 4px 15px rgba(16, 185, 129, 0.4)';
            }}
          >
            ➕ New Question Set
          </button>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      {(currentFolderId || folderPath.length > 0) && (
        <FolderBreadcrumb path={folderPath} onNavigate={setCurrentFolder} />
      )}

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => {
          setShowCreateFolderModal(false);
          setFolderToEdit(null);
        }}
        onCreate={folderToEdit ? handleUpdateFolder : handleCreateFolder}
        parentFolderId={currentFolderId}
        folderToEdit={folderToEdit}
      />

      {/* Move Item Modal */}
      {moveItemModal && (
        <MoveToFolderModal
          isOpen={moveItemModal.isOpen}
          onClose={() => setMoveItemModal(null)}
          onMove={handleMoveItem}
          folders={folders}
          currentFolderId={moveItemModal.currentFolderId}
          disabledFolderIds={
            moveItemModal.itemType === 'folder'
              ? getDescendantFolderIds(moveItemModal.itemId)
              : []
          }
          itemName={moveItemModal.itemName}
          itemType={moveItemModal.itemType}
        />
      )}

      {/* Create Question Set Form Modal */}
      {showCreateForm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(8px)',
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '24px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
          >
            <h3
              style={{
                margin: '0 0 1.5rem',
                fontSize: '1.5rem',
                color: '#374151',
                textAlign: 'center',
              }}
            >
              ✨ Create New Question Set
            </h3>

            {currentFolderId && folderPath.length > 0 && (
              <div
                style={{
                  padding: '0.75rem 1rem',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '12px',
                  marginBottom: '1.5rem',
                  fontSize: '0.9rem',
                  color: '#374151',
                }}
              >
                📁 Will be saved in:{' '}
                <strong>
                  {folderPath[folderPath.length - 1]?.name || 'Root'}
                </strong>
              </div>
            )}

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem',
              }}
            >
              {/* Name */}
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem',
                  }}
                >
                  Set Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g., Math Week 4 - Fractions"
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    transition: 'border-color 0.2s ease',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#667eea')}
                  onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                  autoFocus
                />
              </div>

              {/* Description */}
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem',
                  }}
                >
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Brief description of the question set..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    resize: 'vertical',
                    transition: 'border-color 0.2s ease',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#667eea')}
                  onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                />
              </div>

              {/* Difficulty & Tags */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1rem',
                }}
              >
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.5rem',
                    }}
                  >
                    Difficulty
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        difficulty: e.target.value as
                          | 'easy'
                          | 'medium'
                          | 'hard'
                          | 'mixed',
                      }))
                    }
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      background: 'white',
                    }}
                  >
                    <option value="easy">🟢 Easy</option>
                    <option value="medium">🟡 Medium</option>
                    <option value="hard">🔴 Hard</option>
                    <option value="mixed">🌈 Mixed</option>
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.5rem',
                    }}
                  >
                    Tags (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, tags: e.target.value }))
                    }
                    placeholder="math, algebra, basic"
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      transition: 'border-color 0.2s ease',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#667eea')}
                    onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                  />
                  <p
                    style={{
                      margin: '0.25rem 0 0',
                      fontSize: '0.75rem',
                      color: '#64748b',
                    }}
                  >
                    Separate tags with commas
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div
                style={{
                  display: 'flex',
                  gap: '1rem',
                  marginTop: '1rem',
                }}
              >
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setFormData({
                      name: '',
                      description: '',
                      difficulty: 'mixed',
                      tags: '',
                    });
                  }}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = '#e5e7eb')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = '#f3f4f6')
                  }
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSet}
                  disabled={!formData.name.trim()}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: formData.name.trim()
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: formData.name.trim() ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                  }}
                >
                  Create & Edit Questions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Folders and Question Sets Grid */}
      {currentFolders.length === 0 && currentQuestionSets.length === 0 ? (
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            padding: '4rem 2rem',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
            {currentFolderId ? '📁' : '📝'}
          </div>
          <h3 style={{ color: '#374151', marginBottom: '1rem' }}>
            {currentFolderId ? 'Empty Folder' : 'No Question Sets Yet'}
          </h3>
          <p
            style={{
              color: '#64748b',
              marginBottom: '2rem',
              maxWidth: '400px',
              margin: '0 auto 2rem',
            }}
          >
            {currentFolderId
              ? 'This folder is empty. Create a question set or add a subfolder to get started.'
              : 'Create your first question set or folder to start organizing your quizzes.'}
          </p>
          <div
            style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}
          >
            <button
              onClick={() => setShowCreateFolderModal(true)}
              style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow =
                  '0 6px 20px rgba(245, 158, 11, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              📁 Create Folder
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow =
                  '0 6px 20px rgba(102, 126, 234, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              🚀 Create Question Set
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
          }}
        >
          {currentFolders.length > 0 && (
            <div
              style={{
                background:
                  'linear-gradient(180deg, rgba(255, 250, 240, 0.92) 0%, rgba(255, 255, 255, 0.92) 100%)',
                borderRadius: '24px',
                padding: '1.25rem',
                border: '1px solid rgba(245, 158, 11, 0.22)',
                boxShadow: '0 10px 26px rgba(245, 158, 11, 0.12)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  flexWrap: 'wrap',
                  marginBottom: '1rem',
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#d97706',
                      marginBottom: '0.35rem',
                    }}
                  >
                    Folders
                  </div>
                  <h3
                    style={{
                      margin: '0 0 0.25rem',
                      fontSize: '1.15rem',
                      fontWeight: '700',
                      color: '#1f2937',
                    }}
                  >
                    Keep folders as navigation
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '0.92rem',
                      color: '#64748b',
                    }}
                  >
                    Compact folder tiles stay grouped together so question sets
                    remain the main editable cards.
                  </p>
                </div>

                <div
                  style={{
                    padding: '0.5rem 0.8rem',
                    borderRadius: '999px',
                    background: 'rgba(255, 255, 255, 0.78)',
                    border: '1px solid rgba(245, 158, 11, 0.22)',
                    color: '#92400e',
                    fontSize: '0.82rem',
                    fontWeight: '700',
                  }}
                >
                  {currentFolders.length}{' '}
                  {currentFolders.length === 1 ? 'folder' : 'folders'}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  alignItems: 'stretch',
                }}
              >
                {currentFolders.map((folder) => {
                  const counts = getFolderCounts(folder.id);
                  return (
                    <FolderCard
                      key={folder.id}
                      folder={folder}
                      questionSetCount={counts.questionSetCount}
                      subFolderCount={counts.subFolderCount}
                      onOpen={setCurrentFolder}
                      onRename={(f) => {
                        setFolderToEdit(f);
                        setShowCreateFolderModal(true);
                      }}
                      onMove={(f) =>
                        setMoveItemModal({
                          isOpen: true,
                          itemId: f.id,
                          itemName: f.name,
                          itemType: 'folder',
                          currentFolderId: f.parentFolderId || null,
                        })
                      }
                      onDelete={(f) => setDeleteFolderConfirmId(f.id)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {currentQuestionSets.length > 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                background:
                  'linear-gradient(180deg, rgba(245, 247, 255, 0.92) 0%, rgba(255, 255, 255, 0.92) 100%)',
                borderRadius: '24px',
                padding: '1.25rem',
                border: '1px solid rgba(99, 102, 241, 0.18)',
                boxShadow: '0 10px 26px rgba(99, 102, 241, 0.12)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  flexWrap: 'wrap',
                  marginBottom: '0.25rem',
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#4f46e5',
                      marginBottom: '0.35rem',
                    }}
                  >
                    Question Sets
                  </div>
                  <h3
                    style={{
                      margin: '0 0 0.25rem',
                      fontSize: '1.15rem',
                      fontWeight: '700',
                      color: '#1f2937',
                    }}
                  >
                    Main content cards
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '0.92rem',
                      color: '#64748b',
                    }}
                  >
                    These stay larger because they hold the draft state,
                    question count, and editing actions.
                  </p>
                </div>

                <div
                  style={{
                    padding: '0.5rem 0.8rem',
                    borderRadius: '999px',
                    background: 'rgba(255, 255, 255, 0.78)',
                    border: '1px solid rgba(99, 102, 241, 0.18)',
                    color: '#3730a3',
                    fontSize: '0.82rem',
                    fontWeight: '700',
                  }}
                >
                  {currentQuestionSets.length}{' '}
                  {currentQuestionSets.length === 1
                    ? 'question set'
                    : 'question sets'}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1.5rem',
                }}
              >
                {currentQuestionSets.map((set) => (
                  <div
                    key={set.id}
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 255, 0.96) 100%)',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '20px',
                      padding: '1.5rem',
                      boxShadow: '0 12px 30px rgba(99, 102, 241, 0.12)',
                      border:
                        set.status === 'draft'
                          ? '2px dashed rgba(245, 158, 11, 0.5)'
                          : '1px solid rgba(99, 102, 241, 0.12)',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow =
                        '0 16px 36px rgba(99, 102, 241, 0.18)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow =
                        '0 12px 30px rgba(99, 102, 241, 0.12)';
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: '1rem',
                        marginBottom: '1rem',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            flexWrap: 'wrap',
                            marginBottom: '0.65rem',
                          }}
                        >
                          <span
                            style={{
                              padding: '0.28rem 0.65rem',
                              background: 'rgba(99, 102, 241, 0.1)',
                              color: '#4338ca',
                              borderRadius: '999px',
                              fontSize: '0.72rem',
                              fontWeight: '700',
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase',
                              border: '1px solid rgba(99, 102, 241, 0.18)',
                            }}
                          >
                            Question Set
                          </span>
                          {set.status === 'draft' && (
                            <span
                              style={{
                                padding: '0.25rem 0.75rem',
                                background:
                                  'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: 'white',
                                borderRadius: '12px',
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                              }}
                            >
                              📝 Draft
                            </span>
                          )}
                        </div>

                        <h3
                          style={{
                            margin: '0 0 0.5rem',
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                            color: '#374151',
                            lineHeight: '1.3',
                          }}
                        >
                          {set.name}
                        </h3>

                        {set.description && (
                          <p
                            style={{
                              margin: 0,
                              fontSize: '0.9rem',
                              color: '#64748b',
                              lineHeight: '1.4',
                            }}
                          >
                            {set.description}
                          </p>
                        )}
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            background: 'white',
                            padding: '0.375rem',
                            borderRadius: '10px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '1.2rem',
                              padding: '0.25rem',
                            }}
                          >
                            {getDifficultyIcon(set.difficulty)}
                          </span>
                          <button
                            onClick={() =>
                              setMoveItemModal({
                                isOpen: true,
                                itemId: set.id,
                                itemName: set.name,
                                itemType: 'question set',
                                currentFolderId: set.folderId || null,
                              })
                            }
                            style={{
                              background: 'rgba(59, 130, 246, 0.1)',
                              border: '1px solid rgba(59, 130, 246, 0.3)',
                              color: '#3b82f6',
                              cursor: 'pointer',
                              padding: '0.375rem 0.5rem',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background =
                                'rgba(59, 130, 246, 0.2)';
                              e.currentTarget.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                'rgba(59, 130, 246, 0.1)';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                            title="Move to folder"
                          >
                            📦
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(set.id)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              color: '#ef4444',
                              cursor: 'pointer',
                              padding: '0.375rem 0.5rem',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background =
                                'rgba(239, 68, 68, 0.2)';
                              e.currentTarget.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                'rgba(239, 68, 68, 0.1)';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                            title="Delete question set"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: '1rem',
                        marginBottom: '1rem',
                        padding: '1rem',
                        background: 'rgba(102, 126, 234, 0.05)',
                        borderRadius: '12px',
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: '#667eea',
                          }}
                        >
                          {set.totalQuestions}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          Questions
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: '1rem',
                        borderTop: '1px solid rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: '#64748b',
                        }}
                      >
                        Updated {formatDate(set.updatedAt)}
                      </div>

                      <button
                        onClick={() => onNavigateToEditor(set.id)}
                        style={{
                          padding: '0.5rem 1rem',
                          background:
                            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        ✏️ Edit Questions
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : currentFolders.length > 0 ? (
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                padding: '1.5rem',
                border: '1px solid rgba(99, 102, 241, 0.12)',
                color: '#64748b',
                boxShadow: '0 10px 26px rgba(99, 102, 241, 0.08)',
              }}
            >
              This level only contains folders right now. Open a folder or
              create a new question set here.
            </div>
          ) : null}
        </div>
      )}

      {/* Delete Question Set Confirmation Modal */}
      {deleteConfirmId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(8px)',
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '20px',
              padding: '2rem',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🗑️</div>
            <h3
              style={{
                margin: '0 0 1rem',
                fontSize: '1.3rem',
                color: '#374151',
              }}
            >
              Delete Question Set?
            </h3>
            <p
              style={{
                margin: '0 0 2rem',
                color: '#64748b',
                lineHeight: '1.5',
              }}
            >
              This action cannot be undone. All questions in this set will be
              permanently deleted.
            </p>

            <div
              style={{
                display: 'flex',
                gap: '1rem',
              }}
            >
              <button
                onClick={() => setDeleteConfirmId(null)}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = '#e5e7eb')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = '#f3f4f6')
                }
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSet(deleteConfirmId)}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background:
                    'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Confirmation Modal */}
      {deleteFolderConfirmId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(8px)',
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '20px',
              padding: '2rem',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🗑️</div>
            <h3
              style={{
                margin: '0 0 1rem',
                fontSize: '1.3rem',
                color: '#374151',
              }}
            >
              Delete Folder?
            </h3>
            <p
              style={{
                margin: '0 0 2rem',
                color: '#64748b',
                lineHeight: '1.5',
              }}
            >
              This folder must be empty (no question sets or subfolders) before
              it can be deleted.
            </p>

            <div
              style={{
                display: 'flex',
                gap: '1rem',
              }}
            >
              <button
                onClick={() => setDeleteFolderConfirmId(null)}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = '#e5e7eb')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = '#f3f4f6')
                }
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteFolder(deleteFolderConfirmId)}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background:
                    'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                Delete Folder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionManagement;
